/* eslint-disable no-restricted-globals */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-underscore-dangle */
import uuidv4 from 'uuid/v4'
import { DateTime } from 'luxon'

import { SESSION_LENGTH } from 'ducks/global/tracker/constants'
import {
  CLEAR,
  EVENT,
  IDENTIFY_COMPANY,
  IDENTIFY_CONTACT,
  PAGE_UNLOAD,
  PAGE_VIEW,
  SET_COMPANY_PROPERTIES,
  SET_CONTACT_PROPERTIES,
  REFRESH_SESSION,
} from 'ducks/global/tracker/actionTypes/events'

import { MissingEventError } from './errors'
// Fallback for Safari which doesn't support requestIdleCallback
const idleCallback =
  window.requestIdleCallback ||
  (x => {
    x()
  })

const MAX_STRING_LENGTH = 1024

export default class Tracker {
  constructor(shim) {
    this.shim = shim
    const {
      options: { baseEventProperties = {}, mainScrollElement = document } = {},
    } = shim || {}
    this.pageView = this.pageView.bind(this)
    this.pageUnload = this.pageUnload.bind(this)
    this.pageUnloadAutomatic = this.pageUnloadAutomatic.bind(this)
    this.pageScroll = this.pageScroll.bind(this)
    this.isScrolling = false
    this.maxScrollPixels = 0
    this.maxScrollPercent = 0
    window.groove.widget.exec('trackPageView', true)
    this.monkeyPatchPushState()
    window.removeEventListener('beforeunload', this.pageUnloadAutomatic)
    window.addEventListener('beforeunload', this.pageUnloadAutomatic)
    mainScrollElement.removeEventListener('scroll', this.pageScroll)
    mainScrollElement.addEventListener('scroll', this.pageScroll)
    this.mainScrollElement = mainScrollElement
    this.referrer = window.document.referrer
    this.baseEventProperties = baseEventProperties
  }

  init(action) {
    const {
      payload: { client, session },
    } = action
    this.sessionCreatedAt = DateTime.fromISO(session.createdAt)
    this.sessionId = session.id
    this.sessionExpires = DateTime.fromISO(session.expires)
    this.clientId = client.id
  }

  setMainScrollElement(element) {
    if (!element) {
      this.mainScrollElement.removeEventListener('scroll', this.pageScroll)
      document.addEventListener('scroll', this.pageScroll)
      this.mainScrollElement = document
    } else {
      this.mainScrollElement.removeEventListener('scroll', this.pageScroll)
      element.addEventListener('scroll', this.pageScroll)
      this.mainScrollElement = element
    }
  }

  trackEvent(
    event,
    properties = {},
    occurredAt = DateTime.local().toISO(),
    automatic = false
  ) {
    const { options: { trackEvents = true } = {} } = this.shim
    if (trackEvents === false) return null
    if (!event) {
      throw new MissingEventError('You must provide trackEvent with an event')
    }
    return this.shim.dispatchToWidget({
      type: EVENT,
      meta: {
        automatic,
        clientId: this.clientId,
        sessionId: this.sessionId,
      },
      payload: {
        event,
        occurredAt,
        properties: {
          ...this.baseEventProperties,
          ...properties,
        },
      },
    })
  }

  pageView(automatic = false, post = true) {
    this.pageViewId = uuidv4()
    this.pageViewOccurredAt = DateTime.local()
    this.refreshSession()
    const properties = {
      id: this.pageViewId,
      language: navigator.language,
      occurredAt: this.pageViewOccurredAt.toISO(),
      referrer: (this.referrer || '').substring(0, MAX_STRING_LENGTH),
      title: (document.title || '').substring(0, MAX_STRING_LENGTH),
      url: (document.location.href || '').substring(0, MAX_STRING_LENGTH),
      userAgent: navigator.userAgent,
    }
    this.shim.dispatchToWidget({
      type: PAGE_VIEW,
      meta: {
        clientId: this.clientId,
        sessionId: this.sessionId,
        post,
      },
      payload: properties,
    })
    this.trackEvent(
      'page-view',
      properties,
      this.pageViewOccurredAt.toISO(),
      automatic
    )
  }

  pageUnload(automatic = false) {
    const occurredAt = DateTime.local()
    if (!this.pageViewOccurredAt) return
    const timeOnPage = occurredAt
      .diff(this.pageViewOccurredAt, ['seconds'])
      .toObject().seconds
    this.shim.dispatchToWidget({
      type: PAGE_UNLOAD,
      meta: {
        clientId: this.clientId,
        pageViewId: this.pageViewId,
        sessionId: this.sessionId,
      },
      payload: {
        occurredAt: occurredAt.toISO(),
        maxScrollPercent: this.maxScrollPercent,
        maxScrollPixels: this.maxScrollPixels,
        timeOnPage,
      },
    })
    this.trackEvent(
      'page-unload',
      {
        clientId: this.clientId,
        maxScrollPercent: this.maxScrollPercent,
        maxScrollPixels: this.maxScrollPixels,
        pageViewId: this.pageViewId,
        sessionId: this.sessionId,
        timeOnPage,
      },
      occurredAt.toISO(),
      automatic
    )
    this.maxScrollPercent = 0
    this.maxScrollPixels = 0
  }

  pageScroll({ target }) {
    clearTimeout(this.isScrolling)
    this.isScrolling = setTimeout(() => {
      idleCallback(
        () => {
          const { clientHeight, scrollHeight, scrollTop } =
            target === document ? target.documentElement : target
          this.maxScrollPixels =
            scrollTop > this.maxScrollPixels ? scrollTop : this.maxScrollPixels
          const scrollPercent = scrollTop / (scrollHeight - clientHeight)
          this.maxScrollPercent =
            scrollPercent > this.maxScrollPercent
              ? scrollPercent
              : this.maxScrollPercent
        },
        { timeout: 1000 }
      )
    }, 100)
  }

  pageUnloadAutomatic() {
    this.pageUnload(true)
  }

  refreshSession() {
    const isSessionExpired =
      !this.sessionId || this.sessionExpires < DateTime.local()
    this.sessionExpires = DateTime.local().plus(SESSION_LENGTH)
    this.sessionId = isSessionExpired ? uuidv4() : this.sessionId
    const previousSessionCreatedAt = this.sessionCreatedAt
    this.sessionCreatedAt = isSessionExpired
      ? DateTime.local()
      : this.sessionCreatedAt
    this.clientId = this.clientId || uuidv4()
    this.clientCreatedAt = this.clientCreatedAt || DateTime.local()

    this.shim.dispatchToWidget({
      type: REFRESH_SESSION,
      meta: {
        sessionId: this.sessionId,
        clientId: this.clientId,
        previousSessionCreatedAt: previousSessionCreatedAt.toISO(),
      },
      payload: {
        client: {
          id: this.clientId,
          createdAt: this.clientCreatedAt.toISO(),
        },
        session: {
          id: this.sessionId,
          createdAt: this.sessionCreatedAt.toISO(),
          expires: this.sessionExpires.toISO(),
          new: isSessionExpired,
        },
      },
    })
    if (isSessionExpired) {
      this.trackEvent(
        'session-start',
        {
          id: this.sessionId,
          previousSessionTimeOnSite: DateTime.local()
            .diff(previousSessionCreatedAt, ['seconds'])
            .toObject().seconds,
        },
        undefined,
        true
      )
    }
  }

  setBaseEventProperties(properties, { merge = false }) {
    this.baseEventProperties = merge
      ? {
          ...this.baseEventProperties,
          ...properties,
        }
      : properties
  }

  identifyContact(identifyingKey, identifyingValue, properties = null) {
    this.shim.dispatchToWidget({
      type: IDENTIFY_CONTACT,
      meta: {
        clientId: this.clientId,
        pageViewId: this.pageViewId,
        sessionId: this.sessionId,
      },
      payload: { identifyingKey, identifyingValue, properties },
    })
  }

  setContact(properties) {
    this.shim.dispatchToWidget({
      type: SET_CONTACT_PROPERTIES,
      meta: {
        clientId: this.clientId,
        pageViewId: this.pageViewId,
        sessionId: this.sessionId,
      },
      payload: { properties },
    })
  }

  identifyCompany(identifyingKey, identifyingValue, properties = null) {
    this.shim.dispatchToWidget({
      type: IDENTIFY_COMPANY,
      meta: {
        clientId: this.clientId,
        pageViewId: this.pageViewId,
        sessionId: this.sessionId,
      },
      payload: {
        identifyingKey,
        identifyingValue,
        properties,
      },
    })
  }

  setCompany(properties) {
    this.shim.dispatchToWidget({
      type: SET_COMPANY_PROPERTIES,
      meta: {
        clientId: this.clientId,
        pageViewId: this.pageViewId,
        sessionId: this.sessionId,
      },
      payload: { properties },
    })
  }

  logout() {
    this.shim.dispatchToWidget({
      type: CLEAR,
      payload: true,
    })
    this.sessionId = null
    this.refreshSession()
    this.pageView(true, false)
  }

  monkeyPatchPushState() {
    this.originalPushState = history.pushState
    window.addEventListener('popstate', this.handlePopState)
    history.pushState = (state, ...args) => {
      this.maxScrollPixels = 0
      this.maxScrollPercent = 0
      this.referrer = document.location.href
      const result = this.originalPushState.call(
        history,
        {
          ...state,
          GROOVE_TRACKER: { referrer: this.referrer },
        },
        ...args
      )
      window.groove.widget.exec('trackPageUnload', true)
      window.groove.widget.exec('trackPageView', true)
      return result
    }
  }

  handlePopState = event => {
    const referrer =
      event &&
      event.state &&
      event.state.GROOVE_TRACKER &&
      event.state.GROOVE_TRACKER.referrer
    this.referrer = referrer
    window.groove.widget.exec('trackPageView', true)
  }

  destroy() {
    window.removeEventListener('beforeunload', this.pageUnloadAutomatic)
    if (this.mainScrollElement) {
      this.mainScrollElement.removeEventListener('scroll', this.pageScroll)
    }
    window.removeEventListener('popstate', this.handlePopState)
    history.pushState = this.originalPushState
  }
}
